import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersistence from 'vuex-persist'
import { axiosInstance } from '../utils/axios-instance'
import filtering from './modules/filtering'
import favourites from './modules/favourites'
import panel from './modules/panel'
import buy from './modules/buy'
import search from './modules/search'
import gallery from './modules/gallery'
import auth from './modules/auth'
import errors from './modules/errors'

const debug = process.env.NODE_ENV !== 'production'

Vue.use(Vuex)

const vuexLocalStorage = new VuexPersistence({
  storage: window.localStorage,
  modules: ['auth']
})

const store = new Vuex.Store({
  strict: debug,
  modules: { filtering, favourites, panel, buy, search, gallery, auth, errors },
  plugins: [vuexLocalStorage.plugin],
  state: {
    loading: false,
    user: null,
    subscriptions: null,
    itemsToShow: 8,
    currentSlide: null,
    boughtSlide: null,
    categories: [],
    tags: [],
    debugEnabled: false,
    mobileMenuActive: false,
    buyMoreCreditsDisplayed: false
  },
  mutations: {
    getUser (state, payload) {
      state.user = payload
    },
    setSubscriptions (state, payload) {
      state.subscriptions = payload
    },
    setLoading (state, payload) {
      state.loading = payload
    },
    setItemsToShow (state, payload) {
      state.itemsToShow = payload
    },
    setCreditsAmound (state, payload) {
      state.user.credits = payload
    },
    setCurrentSlide (state, payload) {
      state.currentSlide = payload
    },
    setBoughtSlide (state, payload) {
      state.boughtSlide = payload
    },
    setCategories (state, categories) {
      state.categories = categories
    },
    setTags (state, tags) {
      state.tags = tags
    },
    setDebug (state, flag) {
      state.debugEnabled = flag
    },
    toggleMobileMenu (state) {
      state.mobileMenuActive = !state.mobileMenuActive
    },
    setBuyMoreCreditsDisplayed (state, flag) {
      state.buyMoreCreditsDisplayed = flag
    }
  },
  getters: {
    showOnlyMy: state => {
      return state.showOnlyMy
    },
    isSubscriber: state => {
      return !!(state.subscriptions && state.subscriptions.length)
    },
    hasUnlimitedSubscription: state => {
      return (
        state.user.subscriptions && state.user.subscriptions.some(
          sub => sub.status === 'active' && sub.plan.is_unlimited_credits
        )
      )
    },
    isLoading: state => {
      return state.loading
    },
    getItemsToShow: state => {
      return state.itemsToShow
    },
    getCreditsAmount: state => {
      return state.user ? state.user.credits : null
    },
    getCurrentSlide: state => {
      return state.currentSlide
    },
    showSearchDebug: (state, getters) => {
      return state.debugEnabled && getters.internalUser
    },
    internalUser: state => {
      try {
        return state.user.email.split('@')[1] === 'infodiagram.com'
      } catch (e) {
        return false
      }
    },
    shouldDisplayBuyMoreCredits: (state, getters) => {
      return (!state.buyMoreCreditsDisplayed && state.user.credits < 2 && !getters.hasUnlimitedSubscription)
    }
  },
  actions: {
    async getUser ({ commit, state }) {
      if (state.user !== null) {
        return
      }
      try {
        const response = await axiosInstance.get(`${process.env.API_URL}/me/`)
        commit('getUser', response.data)
      } catch (error) {
        console.log(error)
      }
    },
    async getSubscriptions ({ commit }) {
      try {
        const response = axiosInstance.get(`${process.env.API_URL}/subscriptions/`)
        commit('setSubscriptions', response.data)
      } catch (error) {
        console.log(error)
      }
    },
    spendCredits ({ commit, state }, amount) {
      commit('setCreditsAmound', state.user.credits - amount)
    },
    async fetchCategories ({ commit }) {
      const controller = new AbortController()
      const response = await axiosInstance.get('/categories/', { params: { parent: true }, signal: controller.signal })
      commit('setCategories', response.data.results)
    },
    async fetchTags ({ commit }) {
      const controller = new AbortController()
      const response = await axiosInstance.get('/tags/', { signal: controller.signal })
      const tags = response.data.results.map(t => t.tag)
      commit('setTags', tags)
    }
  }
})

export default store
